import { title } from 'change-case'
import { array, func } from 'prop-types'
import { kuiperClassNames, RadioArray, Select } from '@opallabs/kuiper'
import { AnalyticsLayer } from '@opallabs/quanta'
import './styles.scss'
import {
  ContentNamePriority,
  CoverImageDisplayMode,
  PlaceholderColor,
  WeekViewMode,
} from '../../types'
import { useWeekViewSettings } from '../../WeekViewSettingsContext'

const contentCollapsedOptions = [
  { label: 'Hide', value: true },
  { label: 'Show', value: false },
]

// Content mode uses the same underlying `collapseContent` state but with
// the "Content Image" label — the toggle controls whether the content's
// rendered snapshot appears on each ContentCard. Show is default.
const contentImageOptions = [
  { label: 'Hide', value: true },
  { label: 'Show', value: false },
]

const spanInMarketDaysOptions = [
  { label: 'Show', value: true },
  { label: 'Hide', value: false },
]

const namePriorityOptions = [
  { label: 'Content Name', value: ContentNamePriority.Content },
  { label: 'Moment Name', value: ContentNamePriority.Moment },
]

export const coverImageDisplayOptions = [
  { label: 'No Image', value: CoverImageDisplayMode.NoImage },
  { label: 'Standard', value: CoverImageDisplayMode.Standard },
  { label: 'Scaled', value: CoverImageDisplayMode.Scaled },
]

const placeholderOptions = Object.values(PlaceholderColor).map((color) => ({
  label: title(color),
  value: color,
}))

const MenuContent = ({ colorByOptions, menuTrackingCallback }) => {
  const {
    collapseContent,
    colorBy,
    coverImageDisplay,
    namePriority,
    placeholderColor,
    setCollapsedContent,
    setColorBy,
    setCoverImageDisplay,
    setNamePriority,
    setPlaceholderColor,
    setSpanInMarketDays,
    spanInMarketDays,
    viewMode,
  } = useWeekViewSettings()

  const isContentMode = viewMode === WeekViewMode.Content

  const onCollapsedOptionChange = (value, track) => {
    setCollapsedContent(value)
    menuTrackingCallback('content-expansion', track)
  }

  const onImageDisplayChange = (value, track) => {
    setCoverImageDisplay(value)
    menuTrackingCallback('image-display', track)
  }

  const onSpanInMarketDaysChange = (value, track) => {
    setSpanInMarketDays(value)
    menuTrackingCallback('span-in-market-days', track)
  }

  const onNamePriorityChange = (value, track) => {
    setNamePriority(value)
    menuTrackingCallback('name-priority', track)
  }

  if (isContentMode) {
    return (
      <div className={kuiperClassNames('weekview-menu-content')}>
        <div className={kuiperClassNames('fieldset-region')}>
          <h6>In-Market Duration</h6>
          <AnalyticsLayer name="Span In-Market Days Options">
            <RadioArray
              options={spanInMarketDaysOptions}
              onChange={onSpanInMarketDaysChange}
              value={spanInMarketDays}
            />
          </AnalyticsLayer>
        </div>
        <div className={kuiperClassNames('fieldset-region')}>
          <h6>Content Image</h6>
          <AnalyticsLayer name="Content Image Options">
            <RadioArray
              options={contentImageOptions}
              onChange={onCollapsedOptionChange}
              value={collapseContent}
            />
          </AnalyticsLayer>
        </div>
        <div className={kuiperClassNames('fieldset-region')}>
          <h6>Primary Name</h6>
          <AnalyticsLayer name="Primary Name Options">
            <RadioArray
              options={namePriorityOptions}
              onChange={onNamePriorityChange}
              value={namePriority}
            />
          </AnalyticsLayer>
        </div>
        <div className={kuiperClassNames('fieldset-region')}>
          <h6>Always Color Code By</h6>
          <AnalyticsLayer name="Color Coding Options">
            <div className={kuiperClassNames('colorBySelect')}>
              <Select
                options={colorByOptions}
                value={colorBy}
                onSelect={setColorBy}
                theme="light"
                selectedComparator={(option, value) =>
                  option.title === value.title
                }
              />
            </div>
          </AnalyticsLayer>
        </div>
      </div>
    )
  }

  return (
    <div className={kuiperClassNames('weekview-menu-content')}>
      <div className={kuiperClassNames('fieldset-region')}>
        <h6>Content</h6>
        <AnalyticsLayer name="Expansion Options">
          <RadioArray
            options={contentCollapsedOptions}
            onChange={onCollapsedOptionChange}
            value={collapseContent}
          />
        </AnalyticsLayer>
      </div>
      <div className={kuiperClassNames('fieldset-region')}>
        <h6>Cover Images</h6>
        <AnalyticsLayer name="Scale Options">
          <RadioArray
            options={coverImageDisplayOptions}
            onChange={onImageDisplayChange}
            value={coverImageDisplay}
          />
        </AnalyticsLayer>
      </div>
      <div className={kuiperClassNames('fieldset-region')}>
        <h6>Placeholder Color</h6>
        <AnalyticsLayer name="Placeholder Color Options">
          <RadioArray
            options={placeholderOptions}
            onChange={setPlaceholderColor}
            value={placeholderColor}
          />
        </AnalyticsLayer>
      </div>
    </div>
  )
}

MenuContent.propTypes = {
  colorByOptions: array,
  menuTrackingCallback: func.isRequired,
}

export default MenuContent
